/**
 * Обёртка экрана: загрузка, ошибка и содержимое в одном месте.
 *
 * Без неё каждый экран обрабатывает состояния по-своему, и человек видит
 * то спиннер, то пустоту, то сырой текст ошибки.
 */
import type { ReactNode } from 'react';

import { ApiError } from '@/shared/api/client';
import { Button } from '@/shared/ui/kit/Button';

import styles from './kit/Screen.module.css';

interface ScreenProps {
  isLoading: boolean;
  error: unknown;
  children: ReactNode;
  onRetry?: () => void;
}

function describeError(error: unknown): { header: string; description: string } {
  if (error instanceof ApiError) {
    if (error.isExpiredAuth) {
      return { header: 'Сессия устарела', description: 'Закройте и откройте приложение заново.' };
    }
    if (error.isForbidden) {
      return { header: 'Нет доступа', description: error.detail };
    }
    if (error.isUnauthorized) {
      return { header: 'Не удалось войти', description: 'Откройте приложение через бота.' };
    }
  }
  // Детали внутренних сбоев человеку не показываем: они ничего ему
  // не объясняют, а диагностика есть в логах бэкенда.
  return {
    header: 'Что-то пошло не так',
    description: 'Попробуйте обновить через несколько секунд.',
  };
}

export function Screen({ isLoading, error, children, onRetry }: ScreenProps) {
  if (isLoading) {
    return (
      <div className={styles.center}>
        <span className={styles.spinner} role="status" aria-label="Загрузка" />
      </div>
    );
  }

  if (error) {
    const { header, description } = describeError(error);
    return (
      <div className={styles.center}>
        <h1 className={styles.title}>{header}</h1>
        <p className={styles.text}>{description}</p>
        {onRetry ? (
          <Button variant="quiet" size="s" onClick={onRetry}>
            Повторить
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.stack}>{children}</div>
    </div>
  );
}

/** Экран-заглушка: используется вне Telegram и для пустых состояний. */
export function Notice({ header, description }: { header: string; description: string }) {
  return (
    <div className={styles.center}>
      <h1 className={styles.title}>{header}</h1>
      <p className={styles.text}>{description}</p>
    </div>
  );
}
